import { ConversationMessageDateDivider } from '@modules/conversation-message/ConversationMessageDateDivider';
import { ConversationNewMessagesDivider } from '@modules/conversation-message/ConversationNewMessagesDivider';
import { ConversationContainer } from '@modules/conversation/ConversationContainer';
import { ConversationVirtuoso } from '@modules/conversation/ConversationVirtuoso';
import { MessageListContainer } from '@modules/conversation/MessageListContainer';
import { useEditor } from '@modules/editor';
import { IconButton, Stack, Typography, styled } from '@mui/material';
import { useEffect, useRef } from 'react';
import { Loading } from 'src/components/Loading/Loading';
import Iconify from 'src/components/iconify';
import { HeaderIcon } from 'src/layouts/DashboardLayout/shared/styles';
import { useConversationContext } from 'src/provider/ConversationContext';
import { RightMenuHeader } from '../../../shared/styles';
import { IGatheringChatItem } from '../types';
import { GatheringChatInput } from './components/GatheringChatInput';
import { GatheringChatItem } from './components/GatheringChatItem';
import { useGatheringChatMessageReadMutation } from './mutations';
import { useGatheringChatContext } from './useGatheringChatContext';
import { useGatheringChatMessagesQuery } from './useGatheringChatMessagesQuery';

export const GatheringChatList = ({
  isGuest,
  disabled,
  onClose,
}: { isGuest?: boolean; disabled: boolean; onClose(): void }) => {
  const { sessionRef, gatheringChat, editorConfig, handleFileSelect } =
    useGatheringChatContext();
  const { goToMessage, setGoToMessage, scrollEnd } = useConversationContext();

  const markedAsReadRef = useRef(false);
  const { editor, editorLoaded } = useEditor(editorConfig);

  const { readLastGatheringChatMessage } =
    useGatheringChatMessageReadMutation();

  const {
    messages,
    isFetching,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    fetchNextPage,
  } = useGatheringChatMessagesQuery(sessionRef);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    markedAsReadRef.current = false;
  }, [gatheringChat?._id]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (markedAsReadRef.current || !messages?.length) return () => {};
    markedAsReadRef.current = true;

    readLastGatheringChatMessage({
      sessionRef,
      messageId: messages.at(-1)?._id || '',
    });
  }, [readLastGatheringChatMessage, messages, gatheringChat?._id]);

  const itemContent = (index: number, message: any) => (
    <div key={`divider-${message._id}-${index}`}>
      {
        <>
          {!message.isSameDayAsPreviousMessage && (
            <ConversationMessageDateDivider date={message.createdAt} />
          )}
          <GatheringChatItem
            hideAuthor={message.isSameAuthorAsPreviousMessage}
            message={message}
            editor={editor}
            disableActions={disabled}
          />
          {gatheringChat?.hasUnreadMessages &&
            gatheringChat?.lastReadMessage === message._id &&
            messages.length - 1 !== index && <ConversationNewMessagesDivider />}
        </>
      }
    </div>
  );

  return (
    <StyledContainer>
      <RightMenuHeader>
        <Stack direction="row" alignItems="center" gap={2}>
          <HeaderIcon>
            <Iconify icon="tabler:file-text" sx={{ color: 'primary.main' }} />
          </HeaderIcon>
          <Typography variant="h6">
            Chat
            {/*{doc.noteRef*/}
            {/*  ? translate('actions_bar.notes')*/}
            {/*  : translate('actions_bar.docs')}*/}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1}>
          <IconButton onClick={onClose}>
            <Iconify icon="tabler:x" />
          </IconButton>
        </Stack>
      </RightMenuHeader>
      {isFetching || !editorLoaded ? (
        <Loading />
      ) : (
        <ConversationContainer handleFileSelect={handleFileSelect}>
          <MessageListContainer>
            <ConversationVirtuoso<IGatheringChatItem>
              messages={messages}
              goToMessage={goToMessage}
              setGoToMessage={setGoToMessage}
              scrollEnd={scrollEnd}
              isFetching={isFetching}
              isFetchingNextPage={isFetchingNextPage}
              isFetchingPreviousPage={isFetchingPreviousPage}
              hasNextPage={!!hasNextPage}
              hasPreviousPage={!!hasPreviousPage}
              fetchNextPage={fetchNextPage}
              fetchPreviousPage={fetchPreviousPage}
              itemContent={itemContent}
            />
          </MessageListContainer>
          {!disabled && <GatheringChatInput isGuest={isGuest} />}
        </ConversationContainer>
      )}
    </StyledContainer>
  );
};

const StyledContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
}));
